import { c as _c } from "react/compiler-runtime";
import { getOrgAgentsToolsOptions } from '@onefootprint/fetch-dashboard';
import { IcoCopy16, IcoTrash16 } from '@onefootprint/icons';
import { IconButton, InlineAlert, Tooltip, useConfirmationDialog } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import type { Editor } from '@tiptap/core';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ArtifactSuggestions from '../components/suggestion/artifact-suggestions';
import FieldSuggestions from '../components/suggestion/field-suggestions';
import GhostArtifact from '../components/suggestion/ghost-artifact';
import GhostField from '../components/suggestion/ghost-field';
import { TaskMetaSuggestions, TaskNodeSuggestions } from '../components/suggestion/task-suggestions';
import { fromTaskState, toTaskState } from '../components/task-editor/task-adapter';
import TaskEditor from '../components/task-editor/task-editor';
import { findOrphanedToolMentionsInTask } from '../components/tools-view/utils/orphaned-tool-mentions';
import ValidationAlerts from '../components/validation-alerts';
import useBlankReportName from '../hooks/use-blank-report-name';
import { useNodeSave } from '../hooks/use-node-save';
import { takenOutputKeys } from '../store/field-key';
import { useBuilder } from '../store/provider';
import { useNewArtifactSuggestions, useNewFieldSuggestions } from '../store/suggestions';
import type { Task as TaskModel } from '../store/types';
import { DocumentPane } from './document-pane';
import NewReportDialog from './report/new-report-dialog';
import TitleEditor from './title-editor';
type TaskPaneProps = {
  task: TaskModel;
  /** 1-based position in the plan, matching the number the nav shows for this task. */
  position: number;
};

// The runtime's "must produce at least one output" finding. An editor gets it from the task itself, so it
// clears the moment an output or report is added: the server's copy lags a save behind, and would
// flash up over the task until the save lands. A read-only viewer keeps the server's.
const MISSING_OUTPUT = /must produce at least one/;
const TaskPane = t0 => {
  const $ = _c(120);
  const {
    task,
    position
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.task"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    state,
    actions,
    isReadOnly
  } = useBuilder();
  const confirm = useConfirmationDialog();
  const save = useNodeSave(task.id);
  const blankReportName = useBlankReportName();
  const [isAddingReport, setIsAddingReport] = useState(false);
  const newFieldSuggestions = useNewFieldSuggestions(task.id);
  const newArtifactSuggestions = useNewArtifactSuggestions(task.id);
  const bodyEditor = useRef(null);
  const producesNothing = task.fields.length === 0 && task.artifacts.length === 0;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getOrgAgentsToolsOptions();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data: toolData
  } = useQuery(t2);
  let T0;
  let t10;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== actions || $[3] !== confirm || $[4] !== isReadOnly || $[5] !== position || $[6] !== producesNothing || $[7] !== save || $[8] !== state.bundle || $[9] !== state.focusNodeId || $[10] !== t || $[11] !== task || $[12] !== toolData?.tools) {
    const orphaned = findOrphanedToolMentionsInTask(task, state.bundle, toolData?.tools ?? []);
    let t11;
    if ($[22] !== actions || $[23] !== confirm || $[24] !== t || $[25] !== task.id || $[26] !== task.name) {
      t11 = () => {
        confirm.open({
          title: t(_temp),
          description: t(_temp2, {
            name: task.name || t(_temp3)
          }),
          variant: "destructive",
          primaryButton: {
            label: t(_temp4),
            onClick: () => actions.removeNode(task.id)
          },
          secondaryButton: {
            label: t(_temp5)
          }
        });
      };
      $[22] = actions;
      $[23] = confirm;
      $[24] = t;
      $[25] = task.id;
      $[26] = task.name;
      $[27] = t11;
    } else {
      t11 = $[27];
    }
    const handleDelete = t11;
    T0 = DocumentPane;
    t3 = `task-${task.id}`;
    t4 = save;
    if ($[28] !== position || $[29] !== t) {
      t5 = t(_temp6, {
        position
      });
      $[28] = position;
      $[29] = t;
      $[30] = t5;
    } else {
      t5 = $[30];
    }
    const t12 = task.name;
    let t13;
    if ($[31] !== t) {
      t13 = t(_temp7);
      $[31] = t;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    let t14;
    if ($[33] !== t) {
      t14 = t(_temp8);
      $[33] = t;
      $[34] = t14;
    } else {
      t14 = $[34];
    }
    const t15 = !isReadOnly;
    const t16 = state.focusNodeId === task.id;
    let t17;
    if ($[35] !== actions.tasks || $[36] !== task.id) {
      t17 = name => actions.tasks.updateTask(task.id, {
        name
      });
      $[35] = actions.tasks;
      $[36] = task.id;
      $[37] = t17;
    } else {
      t17 = $[37];
    }
    let t18;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = () => bodyEditor.current?.commands.focus("start");
      $[38] = t18;
    } else {
      t18 = $[38];
    }
    if ($[39] !== t13 || $[40] !== t14 || $[41] !== t15 || $[42] !== t16 || $[43] !== t17 || $[44] !== task.name) {
      t6 = <TitleEditor value={t12} placeholder={t13} ariaLabel={t14} editable={t15} autoFocus={t16} onChange={t17} onLeave={t18} />;
      $[39] = t13;
      $[40] = t14;
      $[41] = t15;
      $[42] = t16;
      $[43] = t17;
      $[44] = task.name;
      $[45] = t6;
    } else {
      t6 = $[45];
    }
    if ($[46] !== actions || $[47] !== handleDelete || $[48] !== isReadOnly || $[49] !== t || $[50] !== task) {
      t7 = !isReadOnly && <><Tooltip content={t(_temp9)}><IconButton aria-label={t(_temp10)} icon={IcoCopy16} size="compact" variant="ghost" onClick={() => actions.copyNode(task)} /></Tooltip><Tooltip content={t(_temp11)}><IconButton aria-label={t(_temp12)} icon={IcoTrash16} size="compact" variant="ghost" onClick={handleDelete} /></Tooltip></>;
      $[46] = actions;
      $[47] = handleDelete;
      $[48] = isReadOnly;
      $[49] = t;
      $[50] = task;
      $[51] = t7;
    } else {
      t7 = $[51];
    }
    if ($[52] !== isReadOnly || $[53] !== producesNothing || $[54] !== t) {
      t8 = !isReadOnly && producesNothing && <InlineAlert variant="error">{t(_temp13)}</InlineAlert>;
      $[52] = isReadOnly;
      $[53] = producesNothing;
      $[54] = t;
      $[55] = t8;
    } else {
      t8 = $[55];
    }
    let t19;
    if ($[56] !== isReadOnly) {
      t19 = detail => !isReadOnly && MISSING_OUTPUT.test(detail.message);
      $[56] = isReadOnly;
      $[57] = t19;
    } else {
      t19 = $[57];
    }
    if ($[58] !== t19 || $[59] !== task.id) {
      t9 = <ValidationAlerts nodeId={task.id} hide={t19} />;
      $[58] = t19;
      $[59] = task.id;
      $[60] = t9;
    } else {
      t9 = $[60];
    }
    t10 = orphaned.length > 0 && <InlineAlert variant="warning">{orphaned.length === 1 ? t(_temp14, {
        names: orphaned[0].displayName
      }) : t(_temp15, {
        names: orphaned.map(_temp16).join(", ")
      })}</InlineAlert>;
    $[2] = actions;
    $[3] = confirm;
    $[4] = isReadOnly;
    $[5] = position;
    $[6] = producesNothing;
    $[7] = save;
    $[8] = state.bundle;
    $[9] = state.focusNodeId;
    $[10] = t;
    $[11] = task;
    $[12] = toolData?.tools;
    $[13] = T0;
    $[14] = t10;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    T0 = $[13];
    t10 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t11;
  if ($[61] !== task) {
    t11 = <TaskMetaSuggestions task={task} />;
    $[61] = task;
    $[62] = t11;
  } else {
    t11 = $[62];
  }
  let t12;
  if ($[63] !== task) {
    t12 = toTaskState(task);
    $[63] = task;
    $[64] = t12;
  } else {
    t12 = $[64];
  }
  let t13;
  if ($[65] !== state.bundle) {
    t13 = takenOutputKeys(state.bundle);
    $[65] = state.bundle;
    $[66] = t13;
  } else {
    t13 = $[66];
  }
  let t14;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = editor => {
      bodyEditor.current = editor;
    };
    $[67] = t14;
  } else {
    t14 = $[67];
  }
  let t15;
  if ($[68] !== actions.tasks || $[69] !== task.id) {
    t15 = (update, options) => actions.tasks.editTask(task.id, current => ({
      ...current,
      ...fromTaskState(current, update(toTaskState(current)))
    }), options);
    $[68] = actions.tasks;
    $[69] = task.id;
    $[70] = t15;
  } else {
    t15 = $[70];
  }
  let t16;
  if ($[71] !== task.fields || $[72] !== task.id) {
    t16 = fieldId => {
      const field = task.fields.find(candidate => candidate.id === fieldId);
      return field && <FieldSuggestions taskId={task.id} field={field} />;
    };
    $[71] = task.fields;
    $[72] = task.id;
    $[73] = t16;
  } else {
    t16 = $[73];
  }
  let t17;
  if ($[74] !== task.artifacts || $[75] !== task.id) {
    t17 = artifactId => {
      const artifact = task.artifacts.find(candidate_0 => candidate_0.id === artifactId);
      return artifact && <ArtifactSuggestions taskId={task.id} artifact={artifact} />;
    };
    $[74] = task.artifacts;
    $[75] = task.id;
    $[76] = t17;
  } else {
    t17 = $[76];
  }
  let t18;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setIsAddingReport(true);
    $[77] = t18;
  } else {
    t18 = $[77];
  }
  let t19;
  if ($[78] !== actions) {
    t19 = artifactId_0 => actions.unpickReports([artifactId_0]);
    $[78] = actions;
    $[79] = t19;
  } else {
    t19 = $[79];
  }
  let t20;
  if ($[80] !== isReadOnly || $[81] !== state.highlightedId || $[82] !== t12 || $[83] !== t13 || $[84] !== t15 || $[85] !== t16 || $[86] !== t17 || $[87] !== t19) {
    t20 = <TaskEditor node={t12} readOnly={isReadOnly} takenOutputKeys={t13} highlightedId={state.highlightedId} onBodyEditorReady={t14} onNode={t15} renderFieldSuggestions={t16} renderReportSuggestions={t17} onAddReport={t18} onRemoveReport={t19} />;
    $[80] = isReadOnly;
    $[81] = state.highlightedId;
    $[82] = t12;
    $[83] = t13;
    $[84] = t15;
    $[85] = t16;
    $[86] = t17;
    $[87] = t19;
    $[88] = t20;
  } else {
    t20 = $[88];
  }
  let t21;
  if ($[89] !== newArtifactSuggestions || $[90] !== newFieldSuggestions) {
    t21 = (newFieldSuggestions.length > 0 || newArtifactSuggestions.length > 0) && <div className="flex flex-col gap-2">{newFieldSuggestions.map(_temp17)}{newArtifactSuggestions.map(_temp18)}</div>;
    $[89] = newArtifactSuggestions;
    $[90] = newFieldSuggestions;
    $[91] = t21;
  } else {
    t21 = $[91];
  }
  let t22;
  if ($[92] !== task) {
    t22 = <TaskNodeSuggestions task={task} />;
    $[92] = task;
    $[93] = t22;
  } else {
    t22 = $[93];
  }
  let t23;
  if ($[94] !== t) {
    t23 = t(_temp19);
    $[94] = t;
    $[95] = t23;
  } else {
    t23 = $[95];
  }
  let t24;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => setIsAddingReport(false);
    $[96] = t24;
  } else {
    t24 = $[96];
  }
  let t25;
  if ($[97] !== actions || $[98] !== task.id) {
    t25 = report => actions.startReport(task.id, report);
    $[97] = actions;
    $[98] = task.id;
    $[99] = t25;
  } else {
    t25 = $[99];
  }
  let t26;
  if ($[100] !== blankReportName || $[101] !== isAddingReport || $[102] !== t23 || $[103] !== t25) {
    t26 = <NewReportDialog defaultName={blankReportName} open={isAddingReport} title={t23} onClose={t24} onCreate={t25} />;
    $[100] = blankReportName;
    $[101] = isAddingReport;
    $[102] = t23;
    $[103] = t25;
    $[104] = t26;
  } else {
    t26 = $[104];
  }
  let t27;
  if ($[105] !== T0 || $[106] !== t10 || $[107] !== t11 || $[108] !== t20 || $[109] !== t21 || $[110] !== t22 || $[111] !== t26 || $[112] !== t3 || $[113] !== t4 || $[114] !== t5 || $[115] !== t6 || $[116] !== t7 || $[117] !== t8 || $[118] !== t9) {
    t27 = <T0 id={t3} onBlur={t4} eyebrow={t5} title={t6} actions={t7}>{t8}{t9}{t10}{t11}{t20}{t21}{t22}{t26}</T0>;
    $[105] = T0;
    $[106] = t10;
    $[107] = t11;
    $[108] = t20;
    $[109] = t21;
    $[110] = t22;
    $[111] = t26;
    $[112] = t3;
    $[113] = t4;
    $[114] = t5;
    $[115] = t6;
    $[116] = t7;
    $[117] = t8;
    $[118] = t9;
    $[119] = t27;
  } else {
    t27 = $[119];
  }
  return t27;
};
export default TaskPane;
function _temp($) {
  return $.delete.title;
}
function _temp2($_0) {
  return $_0.delete.description;
}
function _temp3($_1) {
  return $_1.newTask;
}
function _temp4($_2) {
  return $_2.delete.confirm;
}
function _temp5($_3) {
  return $_3.cancel;
}
function _temp6($_4) {
  return $_4.position;
}
function _temp7($_5) {
  return $_5.newTask;
}
function _temp8($_6) {
  return $_6.name.label;
}
function _temp9($_7) {
  return $_7.copy.ariaLabel;
}
function _temp10($_8) {
  return $_8.copy.ariaLabel;
}
function _temp11($_9) {
  return $_9.delete.ariaLabel;
}
function _temp12($_10) {
  return $_10.delete.ariaLabel;
}
function _temp13($_11) {
  return $_11.outputs.required;
}
function _temp14($_12) {
  return $_12.orphanedTool_one;
}
function _temp15($_13) {
  return $_13.orphanedTool_other;
}
function _temp16(tool) {
  return tool.displayName;
}
function _temp17(s) {
  return <GhostField key={s.id} suggestion={s} />;
}
function _temp18(s_0) {
  return <GhostArtifact key={s_0.id} suggestion={s_0} />;
}
function _temp19($_14) {
  return $_14.reports.newTitle;
}